import React, { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { FaCamera, FaTimes, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';

const BarcodeScannerModal = ({ isOpen, onClose, onScanResult }) => {
  const webcamRef = useRef(null);
  const [manualBarcode, setManualBarcode] = useState('');
  const [scanning, setScanning] = useState(false);

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualBarcode.trim()) {
      onScanResult(manualBarcode.trim());
      setManualBarcode('');
      onClose();
    } else {
      toast.error('Please enter a barcode');
    }
  };

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      // In a real implementation, you would send this image to a barcode detection service
      // For now, we'll just show a message
      toast.info('Camera capture taken. Please enter barcode manually for now.');
    }
  }, [webcamRef]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Barcode Scanner</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Manual Barcode Entry */}
          <div>
            <h4 className="text-md font-medium text-gray-800 mb-3">Enter Barcode Manually</h4>
            <form onSubmit={handleManualSubmit} className="flex space-x-2">
              <input
                type="text"
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e.target.value)}
                placeholder="Enter barcode here..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center space-x-2"
              >
                <FaSearch />
                <span>Search</span>
              </button>
            </form>
          </div>

          {/* Camera Scanner */}
          <div>
            <h4 className="text-md font-medium text-gray-800 mb-3">Camera Scanner</h4>
            <div className="bg-gray-100 rounded-lg p-4">
              <div className="aspect-video bg-gray-200 rounded-lg mb-4 overflow-hidden">
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{
                    width: 640,
                    height: 480,
                    facingMode: "environment" // Use back camera on mobile
                  }}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="flex justify-center space-x-4">
                <button
                  onClick={capture}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center space-x-2"
                >
                  <FaCamera />
                  <span>Capture & Scan</span>
                </button>
              </div>
              
              <div className="mt-3 text-sm text-gray-600 text-center">
                <p>Note: Automatic barcode detection is not fully implemented.</p>
                <p>Please use manual entry for now or capture and enter the barcode manually.</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScannerModal;
